import { prettyPrint } from '@base2/pretty-print-object';
import { Spacing } from '@persona-id/design-tokens';
import SearchIcon from '@persona-id/design-tokens/icons/SearchIcon';
import { orderBy } from 'lodash/fp';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { LinkifyExternal } from 'dashboard/components/LinkifyExternal';
import { MonoSpan } from 'dashboard/components/Typography';
import AsyncImage from 'dashboard/components-lib/AsyncImage';
import Input, { InputGroup } from 'design-system/components/Input';
import Sticker from 'design-system/components/Sticker';
import Table, {
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from 'design-system/components/Table';
import { Field, FieldsMap, FieldType, parseField } from 'lib/fields';

const MultiChoiceOptions = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${Spacing[2]};
`;

const CodeBlock = styled(MonoSpan)`
  white-space-collapse: preserve;
`;

interface FieldsTableProps {
  fields: FieldsMap;
  allRedacted?: boolean;
}

const FieldsTable = ({ fields, allRedacted = false }: FieldsTableProps) => {
  const [searchInputText, searchKeysWith] = useState('');
  const [sortDirection, updateSortDirection] = useState<false | 'desc' | 'asc'>(false);
  const sortedAndFilteredFields = useMemo(() => {
    const filteredFields = Object.entries(fields).filter(
      ([fieldName]) =>
        searchInputText === '' || fieldName.toLowerCase().includes(searchInputText.toLowerCase()),
    );

    if (!sortDirection) return filteredFields;
    return orderBy<[string, Field]>('0', sortDirection)(filteredFields);
  }, [sortDirection, searchInputText, fields]);

  const displayField = (field: Field) => {
    if (allRedacted || field.redacted) {
      return <Sticker>Redacted</Sticker>;
    }

    if (field.type === FieldType.File && field.value != null) {
      return (
        <AsyncImage
          allowModalExpand
          height='250px'
          width='initial'
          value={[{ url: field.value?.url, name: field.value?.filename }]}
        />
      );
    }

    if (field.type === FieldType.MultiChoices) {
      return (
        <MultiChoiceOptions>
          {field.value.map((option: string, optionIndex: number) => (
            <Sticker key={optionIndex} variant='primary'>
              {option}
            </Sticker>
          ))}
        </MultiChoiceOptions>
      );
    }

    const parsedField = parseField(field);

    if (typeof parsedField === 'object') {
      return (
        <LinkifyExternal>
          <CodeBlock>
            {prettyPrint(parsedField, {
              indent: '  ',
              inlineCharacterLimit: 40,
            })}
          </CodeBlock>
        </LinkifyExternal>
      );
    }

    return <LinkifyExternal>{parsedField}</LinkifyExternal>;
  };

  return (
    <>
      <InputGroup
        leftIcon={<SearchIcon />}
        size='md'
        onClear={() => {
          searchKeysWith('');
        }}
      >
        <Input
          placeholder='Search by field name'
          value={searchInputText}
          onChange={(e) => searchKeysWith(e.target.value)}
        />
      </InputGroup>
      <Table aria-label='Object fields' style={{ tableLayout: 'fixed', marginTop: Spacing[2] }}>
        <TableHead>
          <TableRow>
            <TableHeaderCell
              id='fields-table__header-key'
              sortable
              sortDirection={sortDirection}
              onClick={() => updateSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')}
              style={{ width: '35%' }}
            >
              Key
            </TableHeaderCell>
            <TableHeaderCell>Value</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedAndFilteredFields.map(([fieldName, field], index) => (
            <TableRow key={[fieldName, index].join('-')}>
              <TableCell breakWord>{fieldName}</TableCell>
              <TableCell breakWord>{displayField(field)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </>
  );
};

export default FieldsTable;
